// --- 타입 레벨 유틸리티 ---

/**
 * 문자열 T를 구분자 S를 기준으로 나누어 문자열 배열 타입으로 만듭니다.
 * 예: Split<'hello-world', '-'> => ['hello', 'world']
 */
type Split<T extends string, S extends string> = T extends `${infer Head}${S}${infer Tail}`
  ? [Head, ...Split<Tail, S>]
  : [T];

/**
 * 문자열 배열 T의 각 요소를 Capitalize<Lowercase<...>> 처리합니다.
 * 예: MapCapitalize<['hello', 'WORLD']> => ['Hello', 'World']
 */
type MapCapitalize<T extends string[]> = T extends [
  infer First extends string,
  ...infer Rest extends string[],
]
  ? [Capitalize<Lowercase<First>>, ...MapCapitalize<Rest>]
  : [];

/**
 * 문자열 배열 T를 구분자 S로 합쳐 하나의 문자열 리터럴 타입으로 만듭니다.
 * 예: Join<['Hello', 'World'], '-'> => 'Hello-World'
 */
type Join<T extends string[], S extends string> = T extends [
  infer First extends string,
  ...infer Rest extends string[],
]
  ? Rest['length'] extends 0
    ? First
    : `${First}${S}${Join<Rest, S>}`
  : '';

export default function capitalizeFirstLetter<T extends string>(
  origin: T,
): Capitalize<Lowercase<T>>;

export default function capitalizeFirstLetter<T extends string, S extends string>(
  origin: T,
  separator: S,
): Join<MapCapitalize<Split<T, S>>, S>;

/**
 * 문자열의 첫 글자를 대문자로, 나머지를 소문자로 변환합니다.
 * separator가 주어지면, separator를 기준으로 문자열을 나누어 각 부분의 첫 글자만 대문자로 변환한 후 다시 합칩니다.
 *
 * @example
 * capitalizeFirstLetter('hello world'); // 'Hello world'
 * capitalizeFirstLetter('hello world', ' '); // 'Hello World'
 * capitalizeFirstLetter('hello-world', '-'); // 'Hello-World'
 */
export default function capitalizeFirstLetter(origin: string, separator?: string): string {
  if (separator && separator.length > 0) {
    return origin
      .split(separator)
      .map(str => capitalizeFirstLetter(str)) // 재귀 호출 시 첫 번째 오버로드(Capitalize)가 적용됩니다.
      .join(separator);
  }

  return (origin.charAt(0).toUpperCase() + origin.slice(1).toLowerCase()) as any;
}
